import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, TestTube, Calendar, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import AddLabTestDialog from './AddLabTestDialog';

const LabTestsTab = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    fetchTests();
  }, []);

  const fetchTests = async () => {
    try {
      const response = await API.get('/medical/lab-tests');
      setTests(response.data);
    } catch (error) {
      console.error('Failed to fetch lab tests:', error);
      toast.error('Failed to load lab tests');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (testData) => {
    try {
      await API.post('/medical/lab-tests', testData);
      toast.success('Lab test added');
      fetchTests();
      setShowAdd(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add lab test');
    }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/medical/lab-tests/${id}`);
      toast.success('Lab test deleted');
      fetchTests();
    } catch (error) {
      toast.error('Failed to delete lab test');
    }
  };

  if (loading) {
    return <div className="space-y-4">{[1, 2].map((i) => <Skeleton key={i} className="h-32 w-full" />)}</div>;
  }

  if (tests.length === 0) {
    return (
      <>
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-[#B8E6D5] rounded-full flex items-center justify-center mb-4">
            <TestTube className="h-8 w-8 text-gray-700" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No lab tests recorded</h3>
          <p className="text-gray-600 mb-4">Track diagnostic tests and results</p>
          <Button onClick={() => setShowAdd(true)} className="bg-[#B8E6D5] hover:bg-[#A0D6C5] text-gray-900">
            <Plus className="h-4 w-4 mr-2" />
            Add Lab Test
          </Button>
        </div>
        <AddLabTestDialog open={showAdd} onClose={() => setShowAdd(false)} onAdd={handleAdd} />
      </>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Labs & Tests</h3>
        <Button onClick={() => setShowAdd(true)} size="sm" className="bg-[#B8E6D5] hover:bg-[#A0D6C5] text-gray-900">
          <Plus className="h-4 w-4 mr-2" />
          Add Lab Test
        </Button>
      </div>

      <div className="space-y-4">
        {tests.map((test) => (
          <div key={test.id} className="border border-gray-200 rounded-lg p-5 bg-white hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-semibold text-gray-900 text-lg">{test.name}</h4>
                <div className="flex items-center text-sm text-gray-600 mt-1">
                  <Calendar className="h-4 w-4 mr-1" />
                  {test.date_performed}
                </div>
              </div>
              {test.follow_up_required && (
                <Badge className="bg-blue-100 text-blue-800">Follow-up Required</Badge>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mb-3">
              <div>
                <span className="text-gray-600 font-medium">Reason:</span>
                <p className="text-gray-900">{test.reason}</p>
              </div>
              {test.follow_up_date && (
                <div>
                  <span className="text-gray-600 font-medium">Follow-up date:</span>
                  <p className="text-gray-900">{test.follow_up_date}</p>
                </div>
              )}
            </div>

            {test.results_summary && (
              <div className="p-3 bg-blue-50 rounded-lg mb-3">
                <p className="text-sm font-medium text-blue-900">Results Summary:</p>
                <p className="text-sm text-blue-700">{test.results_summary}</p>
              </div>
            )}

            {test.complications && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-3">
                <p className="text-sm font-medium text-yellow-900">Complications:</p>
                <p className="text-sm text-yellow-700">{test.complications}</p>
              </div>
            )}

            {test.document_images && test.document_images.length > 0 && (
              <div className="mb-3">
                <Badge variant="outline" className="text-blue-600">
                  <ImageIcon className="h-3 w-3 mr-1" />
                  {test.document_images.length} document{test.document_images.length > 1 ? 's' : ''}
                </Badge>
              </div>
            )}

            <div className="flex justify-end">
              <Button variant="ghost" size="sm" onClick={() => handleDelete(test.id)} className="text-red-600 hover:text-red-700">
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>

      <AddLabTestDialog open={showAdd} onClose={() => setShowAdd(false)} onAdd={handleAdd} />
    </>
  );
};

export default LabTestsTab;